'use client'
import { PhiMask } from '@/components/PhiMask'
import { useTenantSafe } from '../TenantLayout/Client'
import { Tenant } from '@/payload-types'
import type { AnchorHTMLAttributes } from 'react'
import { cn } from '@/utilities/ui'

export const getTenantContactEmail = (tenant?: Tenant): string => {
  return tenant?.contactEmail || 'support@poweredbyash.com'
}

export type TenantContactHrefProps = {
  className?: string
  onClick?: () => void
  onEmailClick?: () => void
  onPhoneClick?: () => void
  showPhone?: boolean
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'onClick' | 'className'>

export const TenantContactHref = ({
  className,
  onClick,
  onEmailClick,
  onPhoneClick,
  showPhone = true,
  ...anchorProps
}: TenantContactHrefProps) => {
  const ctx = useTenantSafe()
  const tenant = ctx?.tenant ?? null
  const email = getTenantContactEmail(tenant ?? undefined)
  const phone = showPhone ? tenant?.contactPhone : undefined

  // If a custom className is provided, use it as-is (it should include hover/active states if needed)
  // Otherwise, use the default with tenant hyperlink hover/active states
  const linkClass = className
    ? cn('underline hover:underline', className)
    : cn(
        'text-tenant-hyperlink underline hover:underline',
        'hover:text-[color-mix(in_oklab,var(--tenant-hyperlink-color)_70%,#000)]',
        'active:text-[color-mix(in_oklab,var(--tenant-hyperlink-color)_45%,#000)]',
      )
  return (
    <>
      <a
        href={`mailto:${email}`}
        className={linkClass}
        onClick={onEmailClick ?? onClick}
        data-ph-capture-attribute-action="Support Link Clicked"
        {...anchorProps}
      >
        <PhiMask as="span" maskPosthog={false}>
          {email}
        </PhiMask>
      </a>
      {phone && (
        <>
          {' '}
          or call{' '}
          <a
            href={`tel:${phone}`}
            className={linkClass}
            onClick={onPhoneClick ?? onClick}
            data-ph-capture-attribute-action="Support Link Clicked"
            {...anchorProps}
          >
            <PhiMask as="span" maskPosthog={false}>
              {phone}
            </PhiMask>
          </a>
        </>
      )}
    </>
  )
}
